/**
 * CertificateGenerator.tsx — Modal de emisión de certificados clínicos.
 *
 * Ficha clínica v3. Reusa el patrón exacto de montaje del modal de confirmar
 * eliminación en ClinicalRecords.tsx (cr-modal-overlay / cr-modal) y las
 * clases de formulario ya definidas para SessionNoteEditor (cr-edit-*).
 *
 * Contexto B — Portal Terapeuta. RPC subyacente (emitir_certificado) valida
 * ownership del paciente server-side — este componente solo arma los params
 * y delega en emitirCertificado (src/lib/clinicalExport/certificateExport.ts).
 */

import React, { useState } from 'react';
import { CERTIFICATE_TYPES } from '../../lib/clinicalRecords/constants';
import { emitirCertificado } from '../../lib/clinicalExport/certificateExport';
import type { EmitirCertificadoParams, CertificateType } from '../../lib/clinicalExport/certificateExport';

interface CertificateGeneratorProps {
  patientId: string;
  therapistId: string;
  patientNombre: string;
  patientApellido: string;
  onClose: () => void;
}

function CertificateGenerator({
  patientId,
  therapistId,
  patientNombre,
  patientApellido,
  onClose,
}: CertificateGeneratorProps) {
  const [tipoCertificado, setTipoCertificado] = useState<CertificateType>(
    CERTIFICATE_TYPES[0].value as CertificateType
  );
  const [destinatario, setDestinatario] = useState('');
  const [fechaDesde, setFechaDesde] = useState('');
  const [fechaHasta, setFechaHasta] = useState('');
  const [textoLibre, setTextoLibre] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const textoLibreVacio = tipoCertificado === 'texto_libre' && !textoLibre.trim();

  async function handleGenerate() {
    // Abrir la ventana ANTES de cualquier await (RPC + carga de pdfmake son
    // async) — si no, el popup blocker de iOS Safari la corta. Ver comentario
    // en EmitirCertificadoParams.win.
    const win = window.open('', '_blank');
    if (!win) {
      setError('Habilitá las ventanas emergentes para generar el certificado.');
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const params: EmitirCertificadoParams = { patientId, therapistId, tipoCertificado, win };

      if (tipoCertificado === 'asistencia') {
        if (destinatario.trim()) params.destinatario = destinatario.trim();
        if (fechaDesde) params.fechaDesde = fechaDesde;
        if (fechaHasta) params.fechaHasta = fechaHasta;
      } else if (tipoCertificado === 'tratamiento_laboral' || tipoCertificado === 'legal_judicial') {
        if (destinatario.trim()) params.destinatario = destinatario.trim();
      } else if (tipoCertificado === 'texto_libre') {
        params.textoLibre = textoLibre.trim();
      }

      await emitirCertificado(params);
      onClose();
    } catch (err) {
      win.close();
      setError(err instanceof Error ? err.message : 'No se pudo generar el certificado.');
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="cr-modal-overlay" role="dialog" aria-modal="true" aria-label="Generar certificado">
      <div className="cr-modal cr-cert-modal">
        <h3 className="cr-modal__title">
          <i className="fas fa-certificate cr-modal__icon" aria-hidden="true"></i>
          Generar certificado
        </h3>
        <p className="cr-modal__body">
          Certificado para <strong>{patientNombre} {patientApellido}</strong>
        </p>

        {error && <p className="cr-edit-error" role="alert">{error}</p>}

        <div className="cr-edit-field cr-edit-field--full">
          <label className="cr-edit-label" htmlFor="cert-tipo">Tipo de certificado</label>
          <select
            id="cert-tipo"
            className="cr-edit-input"
            value={tipoCertificado}
            onChange={(e) => setTipoCertificado(e.target.value as CertificateType)}
          >
            {CERTIFICATE_TYPES.map((opcion) => (
              <option key={opcion.value} value={opcion.value}>{opcion.label}</option>
            ))}
          </select>
        </div>

        {tipoCertificado === 'asistencia' && (
          <>
            <div className="cr-edit-field cr-edit-field--full">
              <label className="cr-edit-label" htmlFor="cert-destinatario-asistencia">Destinatario (opcional)</label>
              <input
                id="cert-destinatario-asistencia"
                className="cr-edit-input"
                type="text"
                value={destinatario}
                onChange={(e) => setDestinatario(e.target.value)}
                placeholder="Ej: A quien corresponda"
              />
            </div>
            <div className="cr-edit-grid">
              <div className="cr-edit-field">
                <label className="cr-edit-label" htmlFor="cert-fecha-desde">Desde (opcional)</label>
                <input
                  id="cert-fecha-desde"
                  className="cr-edit-input"
                  type="date"
                  value={fechaDesde}
                  onChange={(e) => setFechaDesde(e.target.value)}
                />
              </div>
              <div className="cr-edit-field">
                <label className="cr-edit-label" htmlFor="cert-fecha-hasta">Hasta (opcional)</label>
                <input
                  id="cert-fecha-hasta"
                  className="cr-edit-input"
                  type="date"
                  value={fechaHasta}
                  onChange={(e) => setFechaHasta(e.target.value)}
                />
              </div>
            </div>
          </>
        )}

        {tipoCertificado === 'tratamiento_laboral' && (
          <div className="cr-edit-field cr-edit-field--full">
            <label className="cr-edit-label" htmlFor="cert-destinatario-laboral">Destinatario (opcional)</label>
            <input
              id="cert-destinatario-laboral"
              className="cr-edit-input"
              type="text"
              value={destinatario}
              onChange={(e) => setDestinatario(e.target.value)}
              placeholder="Ej: A quien corresponda"
            />
          </div>
        )}

        {tipoCertificado === 'legal_judicial' && (
          <>
            <div className="cr-edit-field cr-edit-field--full">
              <label className="cr-edit-label" htmlFor="cert-destinatario-legal">Destinatario (opcional)</label>
              <input
                id="cert-destinatario-legal"
                className="cr-edit-input"
                type="text"
                value={destinatario}
                onChange={(e) => setDestinatario(e.target.value)}
                placeholder="Ej: Tribunal / Juzgado"
              />
            </div>
            <div className="cr-cert-warning" role="alert">
              <span className="cr-cert-warning__icon" aria-hidden="true">⚠️</span>
              <span>
                Este tipo de certificado todavía no tiene el contenido legal integrado — el PDF generado lo indica
                explícitamente y no tiene validez legal. Contactá al equipo de desarrollo para habilitar esta fase
                antes de emitir certificados con fines legales o judiciales.
              </span>
            </div>
          </>
        )}

        {tipoCertificado === 'texto_libre' && (
          <div className="cr-edit-field cr-edit-field--full">
            <label className="cr-edit-label" htmlFor="cert-texto-libre">Texto del certificado</label>
            <textarea
              id="cert-texto-libre"
              className="cr-edit-input cr-edit-input--textarea"
              rows={6}
              value={textoLibre}
              onChange={(e) => setTextoLibre(e.target.value)}
              placeholder="Redactá el contenido del certificado…"
            />
          </div>
        )}

        <div className="cr-modal__actions">
          <button
            className="cr-btn cr-btn--primary"
            onClick={handleGenerate}
            disabled={loading || textoLibreVacio}
          >
            {loading ? 'Generando…' : 'Generar'}
          </button>
          <button className="cr-btn cr-btn--ghost" onClick={onClose} disabled={loading}>
            Cancelar
          </button>
        </div>
      </div>
    </div>
  );
}

export default CertificateGenerator;
